<template>
  <div class="wrap">
    <header class="top">
      <div><b>Flow Designer</b> <small>({{ user?.username }})</small></div>
      <div class="actions">
        <button @click="createNew">New Flow</button>
        <button @click="refresh">Refresh</button>
        <button class="ghost" @click="$router.push('/manager')">Back</button>
      </div>
    </header>

    <main class="grid">
      <!-- List -->
      <section class="card">
        <div class="cardHead"><b>Flows</b></div>

        <div class="list">
          <button
            v-for="f in flows"
            :key="f.id"
            class="item"
            :class="{ on: f.id === activeId }"
            @click="openFlow(f.id)"
          >
            <div class="row">
              <b>{{ f.name }}</b>
              <span class="pill" :class="f.is_active ? 'on' : 'off'">
                {{ f.is_active ? 'PUBLISHED' : 'DRAFT' }}
              </span>
            </div>
            <small>ID: {{ f.id }}</small>
          </button>
        </div>
      </section>

      <!-- Editor -->
      <section class="card">
        <div class="cardHead row" style="justify-content:space-between;">
          <b>Editor</b>
          <div class="row" style="gap:8px;">
            <button v-if="activeId !== null" @click="save">Save</button>
            <button v-if="activeId && activeId !== -1" class="danger" @click="togglePublish">
              {{ form.is_active ? "Unpublish" : "Publish" }}
            </button>
          </div>
        </div>

        <div v-if="activeId === null" class="empty">Select a flow or create new.</div>

        <div v-else class="editor">
          <div class="field">
            <label>Name</label>
            <input v-model="form.name" />
          </div>

          <div class="field">
            <label>Start Step ID</label>
            <input v-model="form.start" placeholder="welcome" />
          </div>

          <div class="stepsHead row">
            <b>Steps</b>
            <button class="ghost" @click="addStep">+ Add Step</button>
          </div>

          <div class="steps">
            <div v-for="(step, id) in form.steps" :key="id" class="step">
              <div class="row" style="justify-content:space-between;">
                <b>{{ id }}</b>
                <button class="ghost" @click="removeStep(id)">Remove</button>
              </div>

              <div class="field">
                <label>Type</label>
                <select v-model="step.type">
                  <option value="message">message</option>
                  <option value="question">question</option>
                  <option value="choice">choice</option>
                  <option value="handoff">handoff</option>
                </select>
              </div>

              <div class="field">
                <label>Text</label>
                <textarea v-model="step.text" rows="3"></textarea>
              </div>

              <div v-if="step.type === 'question' || step.type === 'choice'" class="field">
                <label>saveAs (key)</label>
                <input v-model="step.saveAs" placeholder="order_id / topic ..." />
              </div>

              <div v-if="step.type === 'message' || step.type === 'question'" class="field">
                <label>Next Step</label>
                <input v-model="step.next" placeholder="route_menu" />
              </div>

              <div v-if="step.type === 'choice'" class="field">
                <label>Choices</label>
                <div class="choices">
                  <div v-for="(c, i) in step.choices" :key="i" class="choice">
                    <input
                      v-model="c.matchStr"
                      placeholder="match words (comma separated) e.g. 1,order,status"
                    />
                    <input v-model="c.next" placeholder="next step id" />
                    <button class="ghost" @click="step.choices.splice(i, 1)">x</button>
                  </div>
                  <button class="ghost" @click="addChoice(step)">+ Add Choice</button>
                </div>

                <div class="field">
                  <label>Fallback Text</label>
                  <input v-model="step.fallbackText" placeholder="Please type 1,2,3" />
                </div>
              </div>

              <div v-if="step.type === 'handoff'" class="field">
                <label>Mode</label>
                <select v-model="step.mode">
                  <option value="AUTO_ASSIGN">AUTO_ASSIGN</option>
                </select>
              </div>
            </div>
          </div>

          <div class="jsonBox">
            <div class="row" style="justify-content:space-between;">
              <b>JSON Preview</b>
              <button class="ghost" @click="copyJson">Copy</button>
            </div>
            <pre>{{ compiledJson }}</pre>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/services/auth";
import VARS from "@/services/vars"

const router = useRouter();
const user = ref(auth.user);
const API = VARS.API_URL;

const flows = ref([]);
const activeId = ref(null);

const form = ref({
  name: "",
  is_active: 0,
  start: "welcome",
  steps: {},
});

function getTenantId() {
  return auth.user?.tenant_id || null;
}

function toApiFlow() {
  const steps = {};

  for (const [id, s] of Object.entries(form.value.steps || {})) {
    const step = { ...s };

    if (step.type === "choice") {
      step.choices = (step.choices || []).map((c) => ({
        match: String(c.matchStr || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean),
        next: c.next || "",
      }));
    }

    steps[id] = step;
  }

  return {
    start: form.value.start,
    steps,
  };
}

const compiledJson = computed(() => JSON.stringify(toApiFlow(), null, 2));

async function apiGet(path) {
  const r = await fetch(API + path, {
    headers: {
      Authorization: "Bearer " + auth.token,
    },
  });

  if (r.status === 401) {
    router.push("/");
    throw new Error("UNAUTHORIZED");
  }

  const data = await r.json();

  if (!r.ok || !data.ok) {
    throw new Error(data?.message || "API_ERROR");
  }

  return data;
}

async function apiPost(path, body) {
  const r = await fetch(API + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify(body || {}),
  });

  if (r.status === 401) {
    router.push("/");
    throw new Error("UNAUTHORIZED");
  }

  const data = await r.json();

  if (!r.ok || !data.ok) {
    throw new Error(data?.message || "API_ERROR");
  }

  return data;
}

async function apiPut(path, body) {
  const r = await fetch(API + path, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify(body || {}),
  });

  if (r.status === 401) {
    router.push("/");
    throw new Error("UNAUTHORIZED");
  }

  const data = await r.json();

  if (!r.ok || !data.ok) {
    throw new Error(data?.message || "API_ERROR");
  }

  return data;
}

async function refresh() {
  try {
    const data = await apiGet("/api/manager/flows");
    flows.value = Array.isArray(data?.flows) ? data.flows : [];
  } catch (e) {
    console.error("refresh error:", e);
    flows.value = [];
  }
}

function createNew() {
  activeId.value = -1;

  form.value = {
    name: "New Flow",
    is_active: 0,
    start: "welcome",
    steps: {
      welcome: {
        type: "message",
        text: "Hi! Choose 1/2/3",
        next: "route_menu",
      },
      route_menu: {
        type: "choice",
        text: "Type 1/2/3",
        saveAs: "topic",
        choices: [],
        fallbackText: "Type 1,2,3",
      },
      handoff: {
        type: "handoff",
        text: "Connecting to agent…",
        mode: "AUTO_ASSIGN",
      },
    },
  };
}

async function openFlow(id) {
  router.push(`/manager/flows/${id}/designer`);
}

function addStep() {
  const id = prompt("Step ID (example: ask_name)");
  if (!id) return;

  if (form.value.steps[id]) {
    alert("Step already exists");
    return;
  }

  form.value.steps[id] = {
    type: "message",
    text: "",
    next: "",
  };
}

function removeStep(id) {
  if (!confirm("Remove step " + id + "?")) return;
  delete form.value.steps[id];
}

function addChoice(step) {
  if (!step.choices) step.choices = [];
  step.choices.push({
    matchStr: "",
    next: "",
  });
}

async function save() {
  try {
    const payload = {
      name: form.value.name,
      is_active: form.value.is_active ? 1 : 0,
      definition_json: toApiFlow(),
    };

    if (activeId.value === -1) {
      payload.tenant_id = getTenantId();

      if (!payload.tenant_id && auth.user?.role !== "admin") {
        alert("Tenant not found for current user");
        return;
      }

      const r = await apiPost("/api/manager/flows", payload);
      await refresh();
      await openFlow(r.id);
      return;
    }

    await apiPut(`/api/manager/flows/${activeId.value}`, payload);
    await refresh();
    alert("Flow saved");
  } catch (e) {
    console.error("save error:", e);
    alert(e.message || "Save failed");
  }
}

async function togglePublish() {
  try {
    if (!activeId.value || activeId.value === -1) return;

    await apiPost(`/api/manager/flows/${activeId.value}/toggle`, {});
    await openFlow(activeId.value);
    await refresh();
  } catch (e) {
    console.error("togglePublish error:", e);
    alert(e.message || "Publish toggle failed");
  }
}

async function copyJson() {
  try {
    await navigator.clipboard.writeText(compiledJson.value);
    alert("Copied JSON");
  } catch (e) {
    console.error("copyJson error:", e);
  }
}

onMounted(async () => {
  if (!auth.token) return router.push("/");
  if (!["admin", "manager"].includes(auth.user?.role)) return router.push("/inbox");
  await refresh();
});
</script>

<style scoped>
.wrap { font-family: Arial; }
.top { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-bottom:1px solid #eee; }
.actions { display:flex; gap:8px; }
button { padding:8px 10px; border:none; border-radius:10px; background:#0b66d1; color:#fff; cursor:pointer; }
button.ghost { background:#eee; color:#000; }
button.danger { background:#e74c3c; }
.grid { display:grid; grid-template-columns: 320px 1fr; gap:12px; padding:12px; }
.card { border:1px solid #eee; border-radius:12px; overflow:hidden; }
.cardHead { padding:10px 12px; border-bottom:1px solid #eee; }
.list { padding:12px; display:flex; flex-direction:column; gap:8px; }
.item { text-align:left; background:#fafafa; color:#000; border:1px solid #eee; }
.item.on { border-color:#0b66d1; }
.row { display:flex; align-items:center; gap:8px; }
.pill { margin-left:auto; padding:2px 8px; border-radius:999px; font-size:11px; border:1px solid #ddd; }
.pill.on { background:#e9f6ee; border-color:#bfe6cc; }
.pill.off { background:#f5f5f5; border-color:#ddd; }
.empty { padding:16px; color:#777; }
.editor { padding:12px; display:flex; flex-direction:column; gap:12px; }
.field label { display:block; font-size:12px; color:#666; margin-bottom:4px; }
.field input, .field select, .field textarea { width:100%; padding:10px; border:1px solid #ccc; border-radius:10px; }
.stepsHead { padding-top:4px; }
.steps { display:flex; flex-direction:column; gap:10px; }
.step { border:1px solid #eee; border-radius:12px; padding:10px; background:#fff; }
.choices { display:flex; flex-direction:column; gap:8px; }
.choice { display:grid; grid-template-columns: 1fr 200px 40px; gap:8px; }
.jsonBox { border:1px solid #eee; border-radius:12px; padding:10px; background:#fafafa; }
pre { margin:0; font-size:12px; overflow:auto; max-height:260px; }
@media (max-width: 860px) {
  .grid { grid-template-columns: 1fr; }
  .choice { grid-template-columns: 1fr; }
}
</style>